// cspell:ignore Kravetz
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    Autocomplete,
    AutocompleteProps,
    AutocompleteRenderInputParams,
    FormControl,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import React from 'react';
import { FieldError } from 'react-hook-form';
import { EColors, getColor } from 'theme';

const ITEM_HEIGHT = 34;
const ITEM_COUNT = 5;
// Matches SelectField's menu z-index so the popper clears a RightDrawer, which sits above
// `theme.zIndex.modal` to render over the side nav (see Drawer/RightDrawer.tsx).
const POPPER_Z_INDEX = 1350;

export type AutocompleteFieldOption = { value: number | string; label: string; secondaryLabel?: string };

const DIACRITICS_PATTERN = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');

const normalize = (text: string) => text.normalize('NFD').replace(DIACRITICS_PATTERN, '').toLowerCase();

// Split on anything that isn't a letter or digit, so a hyphen or an apostrophe separates words
// too and "jane" finds "Mary-Jane".
const toWords = (text: string) =>
    normalize(text)
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean);

// MUI's default filter matches the whole query as one substring against the whole label, so a
// query typed in a different word order than the label (e.g. "Smith John" for "John Smith")
// never matches. Match each term independently, in any order, instead.
//
// Each term must match the START of a word rather than appear anywhere in the label: people
// type a name expecting the names beginning that way, and matching mid-word brought back every
// name that merely contained the letter somewhere, which reads as no filtering at all.
const filterOptions = (options: AutocompleteFieldOption[], { inputValue }: { inputValue: string }) => {
    const terms = toWords(inputValue);
    if (terms.length === 0) return options;
    return options.filter(option => {
        const words = toWords(option.label);
        return terms.every(term => words.some(word => word.startsWith(term)));
    });
};

const StyledFormControl = styled(FormControl)<{ disabled?: boolean }>(({ disabled }) => ({
    width: '100%',
    backgroundColor: disabled ? getColor(EColors.drWhite) : getColor(EColors.pureWhite),
    borderRadius: '12px',
    pointerEvents: disabled ? 'none' : 'auto',
}));

const BaseAutocomplete = (props: AutocompleteProps<AutocompleteFieldOption, false, false, false>) => (
    <Autocomplete {...props} />
);

const StyledAutocomplete = styled(BaseAutocomplete, {
    shouldForwardProp: prop => prop !== 'error' && prop !== 'disabled',
})<{ error?: boolean; disabled?: boolean }>(({ error, disabled }) => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
        height: '48px',
        backgroundColor: disabled ? getColor(EColors.drWhite) : getColor(EColors.v3Background),
        borderRadius: '12px',
        padding: '4px 9px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${error ? getColor(EColors.red) : getColor(EColors.v3Border)}`,
        borderRadius: '12px',
    },
    '& .MuiInputBase-input': {
        padding: '12.5px',
        paddingLeft: '16px',
        fontSize: '0.875rem',
        color: disabled ? getColor(EColors.pureBlack, 0.38) : getColor(EColors.pureBlack),
        WebkitTextFillColor: disabled ? getColor(EColors.pureBlack, 0.38) : getColor(EColors.pureBlack),
    },
    '& .MuiInputLabel-root': {
        transform: 'translate(16px, 14px)',
        fontSize: '0.875rem',
        lineHeight: '1.5em',
        color: getColor(EColors.pureBlack, 0.5),
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        color: getColor(EColors.pureBlack, 0.6),
        fontSize: '0.875rem',
        transform: 'translate(9px, -50%) scale(1)',
        backgroundColor: getColor(EColors.pureWhite),
        padding: '0 5px',
        lineHeight: '1em',
        top: 0,
    },
    '& .MuiAutocomplete-popupIndicator': {
        color: disabled ? getColor(EColors.pureBlack, 0.38) : getColor(EColors.pureBlack, 0.5),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: getColor(EColors.v3Dark) + ' !important',
    },
}));

const StyledOption = styled('li')(({ theme: { spacing } }) => ({
    '&.MuiAutocomplete-option': {
        margin: spacing(0, 1.5),
        padding: spacing(1),
        fontSize: '0.75rem',
        borderRadius: '4px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '2px',
    },
    '&.MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: getColor(EColors.pureBlack),
        color: getColor(EColors.pureWhite),
    },
    '&.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
        backgroundColor: getColor(EColors.pureBlack, 0.9),
        color: getColor(EColors.pureWhite),
    },
}));

// Dims via opacity rather than a fixed colour so it stays legible against both the
// default white row and the black selected row.
const StyledSecondaryLabel = styled('span')({
    fontSize: '0.6875rem',
    opacity: 0.6,
});

type AutocompleteFieldProps = {
    label?: string;
    error?: FieldError;
    value?: string | number;
    options: AutocompleteFieldOption[];
    disabled?: boolean;
    required?: boolean;
    placeholder?: string;
    noOptionsText?: string;
    onChange?: (value?: string | number) => void;
    onBlur?: () => void;
};

/** Searchable single-select. Same `{ value, label }` option shape as SelectField, but the
 * closed field is a real text input, so a long list is narrowed by typing a whole word of a
 * label — any word, in any order — rather than by Select's first-letter jump. */
export const AutocompleteField = React.forwardRef<HTMLDivElement, AutocompleteFieldProps>(
    (
        {
            label,
            error,
            value,
            options,
            disabled,
            required,
            placeholder,
            noOptionsText = 'No matches',
            onChange,
            onBlur,
        },
        ref
    ) => {
        // Callers can hand us the same choice more than once — teams/{id}/members returns a row
        // per membership rather than per user, so one person with several memberships arrives
        // several times over. Two options sharing a value are indistinguishable to the caller,
        // since picking either emits the same value, and rendering both gives them the same
        // React key: reconciliation then breaks and rows survive a query that no longer matches
        // them (typing "ade" left "Shana Kravetz" on screen). Collapse them before render.
        const uniqueOptions = React.useMemo(() => {
            const seen = new Set<AutocompleteFieldOption['value']>();
            return options.filter(({ value: optionValue }) => {
                if (seen.has(optionValue)) return false;
                seen.add(optionValue);
                return true;
            });
        }, [options]);

        const selectedOption = React.useMemo(
            () => uniqueOptions.find(option => option.value === value) ?? null,
            [uniqueOptions, value]
        );

        // Two teammates can share a display name, which renders as identical rows with no way
        // to tell them apart. Show the secondary label only for those, so the common case of
        // unique names stays a single uncluttered line.
        const duplicatedLabels = React.useMemo(() => {
            const seen = new Set<string>();
            const duplicated = new Set<string>();
            uniqueOptions.forEach(({ label }) => (seen.has(label) ? duplicated.add(label) : seen.add(label)));
            return duplicated;
        }, [uniqueOptions]);

        const handleChange = (_: React.SyntheticEvent, option: AutocompleteFieldOption | null) => {
            onChange?.(option?.value);
        };

        return (
            <StyledFormControl ref={ref} required={required} disabled={disabled}>
                <StyledAutocomplete
                    options={uniqueOptions}
                    value={selectedOption}
                    onChange={handleChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    error={!!error}
                    getOptionLabel={option => option.label}
                    // MUI keys options by label when this is omitted, so teammates who share a
                    // display name collide on their React key and the list mis-renders.
                    getOptionKey={option => option.value}
                    isOptionEqualToValue={(option, selected) => option.value === selected.value}
                    filterOptions={filterOptions}
                    popupIcon={<KeyboardArrowDownIcon />}
                    noOptionsText={noOptionsText}
                    slotProps={{ popper: { sx: { zIndex: POPPER_Z_INDEX } } }}
                    ListboxProps={{ style: { maxHeight: ITEM_HEIGHT * ITEM_COUNT } }}
                    renderOption={({ key, ...props }, option) => (
                        <StyledOption key={key} {...props}>
                            <span>{option.label}</span>
                            {!!option.secondaryLabel && duplicatedLabels.has(option.label) && (
                                <StyledSecondaryLabel>{option.secondaryLabel}</StyledSecondaryLabel>
                            )}
                        </StyledOption>
                    )}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField {...params} label={label} placeholder={placeholder} error={!!error} />
                    )}
                />
                {!!error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500} sx={{ mt: 0.5 }}>
                        {error.message}
                    </Typography>
                )}
            </StyledFormControl>
        );
    }
);

AutocompleteField.displayName = 'AutocompleteField';
